import {
  Arrow,
  Content,
  PopoverContentProps,
  Portal,
  PopoverPortalProps,
} from '@radix-ui/react-popover';
import { forwardRef, ReactNode } from 'react';
import styled from 'styled-components';
import { body2, borderColors, colors, link2, spacing, zIndex } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { PopoverTitle } from './PopoverTitle';
import { PopoverVariants } from './constants';

const StyledContent = styled(Content)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  max-height: ${pxToRem(400)};
  max-width: ${pxToRem(400)};
  z-index: ${zIndex.dropdown};
  background-color: ${colors.white};
  border-radius: ${spacing.threeEights};
  border: ${spacing.single} solid ${colors.grey10};
  filter: drop-shadow(rgba(0, 0, 0, 0.08) 0px 6px 12px);

  > div {
    &:first-of-type {
      padding-top: ${spacing.base};
      border-radius: ${spacing.threeEights} ${spacing.threeEights} 0 0;
    }
    
    &:last-of-type {
      padding-bottom: ${spacing.base};
      border-radius: 0 0 ${spacing.threeEights} ${spacing.threeEights};
    }
  }

  &[data-variant=${PopoverVariants.Segmented}] {
    gap: 0;
  }
`;

const BodyContainer = styled.div`
  ${body2}
  display: flex;
  flex-direction: column;
  overflow: auto;
  width: 100%;
  color: ${colors.secondaryText};
  box-sizing: border-box;
  
  [data-variant=${PopoverVariants.Segmented}] & {
    padding-top: ${spacing.base};
    background-color: ${colors.backgroundSecondary};
    border-top: ${spacing.single} solid ${borderColors.borderPrimary};  
  }
  
  > div {
    max-height: 100%;
    overflow: auto;
    padding-right: ${spacing.base};
    padding-left: ${spacing.base};
  }
`;

const LinkContainer = styled.div`
  ${link2}
  padding-right: ${spacing.base};
  padding-left: ${spacing.base};
`;

/** styled `span` so other elements can be accessed as `div` and :nth-of-type selectors */
const StyledArrow = styled(Arrow)`
  fill: ${colors.white};
`;

export interface Props extends PopoverContentProps {
  /**
   * An optionally provided React node that will include some preset stylings for titles.
   * Those styles can be overriden by the child node provided, if necessary.
   */
  TitleContent?: ReactNode;
  /**
   * An optionally provided React node that will include some preset stylings for links.
   * Those styles can be overriden by the child node provided, if necessary.
   */
  LinkContent?: ReactNode;
  /** Flag to show an arrow pointing to trigger or related content */
  showArrow?: boolean;
  /** Additional text content for Popover */
  subtitle?: string;
  /** Style of Popover */
  variant?: PopoverVariants;
  /** An optional container for the Portal.
   * This can be used if you want the content to be rendered within the relative parent component
   */
  container?: PopoverPortalProps['container'];
}

export const PopoverContent = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      TitleContent,
      LinkContent,
      showArrow = true,
      variant = PopoverVariants.Default,
      subtitle,
      container,
      ...props
    },
    forwardedRef,
  ) => {
    const isSegmented = variant === PopoverVariants.Segmented;
    return (
      <Portal container={container}>
        <StyledContent
          data-portal-id="popover-portal"
          data-test="popover-content"
          data-variant={variant}
          sideOffset={5}
          {...props}
          ref={forwardedRef}
        >
          {TitleContent && (
            <PopoverTitle LinkContent={isSegmented ? LinkContent : undefined} subtitle={subtitle}>
              {TitleContent}
            </PopoverTitle>
          )}
          <BodyContainer>
            <div>{children}</div>
          </BodyContainer>
          {Boolean(LinkContent && !isSegmented) && <LinkContainer>{LinkContent}</LinkContainer>}
          {showArrow && <StyledArrow width={20} height={8} />}
        </StyledContent>
      </Portal>
    );
  },
);
